import { createRouter, createWebHistory } from 'vue-router';
import { cookiemixin } from '../mixins/cookiemixin';

// Key pages in the typical user flows which need to be in the main bundle
import HomePage from '../components/home-page.vue';
import PPage from '../components/p.vue';
import GoodRxComparisonPage from '../components/goodrx-comparison/goodrx-comparison-page.vue';
import CouponDetailsPage from '../components/coupon-details-page.vue';
import ManufacturerCouponPage from '../components/manufacturer-coupon-page.vue';
import AlternativeMedicationPage from '../components/alternative-medication-page.vue';
import SavingsForMembers from '../components/employer-program/savings-for-members.vue';
import SavingsForEmployers from '../components/employer-program/savings-for-employers.vue';
import BenefitConsultants from '../components/employer-program/benefit-consultants.vue';
import ForPayers from '../components/employer-program/for-payers.vue';
import PBMs from '../components/employer-program/health-plans.vue';
import RequestADemo from '../components/request-a-demo.vue';
import CouponAPI from '../components/coupon-api/coupon-api.vue';
import APIContact from '../components/coupon-api/api-contact.vue';

// Landing pages which ?need to be in the main bundle?
import TechLandingPage from '../components/land/tech-landing-page.vue';
import ReimburseMe from '../components/reimburse-me.vue';
import PrescriptionTransfer from '../components/prescription-transfer.vue';

// Pages you can reach from the navigation buttons which ?do NOT need to be in the main bundle?
const HowItWorksPage = () => import(/* webpackChunkName: "navable-pages" */ '../components/how-it-works-page.vue');
const AboutPage = () => import(/* webpackChunkName: "navable-pages" */ '../components/about-page.vue');
const ConsumerDiscountCardsPage = () => import(/* webpackChunkName: "navable-pages" */ '../components/consumer-discount-cards-page.vue');
const ContactPage = () => import(/* webpackChunkName: "navable-pages" */ '../components/contact-page.vue');
const BlogPost = () => import(/* webpackChunkName: "navable-pages" */ '../components/blog-post.vue');
const CVSSavingsFinderComparison = () => import(/* webpackChunkName: "navable-pages" */ '../components/cvs-savings-finder-comparison/cvs-savings-finder-comparison.vue');
const OptumPerksComparison = () => import(/* webpackChunkName: "navable-pages" */ '../components/optum-perks-comparison/optum-perks-comparison.vue');

// AWS Marketplace pages
const AWSMarketplaceRegisterCustomer = () => import(/* webpackChunkName: "aws-marketplace" */ '../components/aws-marketplace/register-customer.vue');
const AWSMarketplaceResolveCustomerError = () => import(/* webpackChunkName: "aws-marketplace" */ '../components/aws-marketplace/resolve-customer-error.vue');

// Caremark has its own, separate APIs and its own monitoring. Consequently, it needs its own admin pages
const CaremarkIntelligentRxAdmin = () => import(/* webpackChunkName: "caremark-admin" */ '../components/caremark/intelligentrx-admin.vue');

// Uncommon pages
const LocalPage = () => import(/* webpackChunkName: "less-common" */ '../components/local-page.vue');
const CareersPage = () => import(/* webpackChunkName: "less-common" */ '../components/careers-page.vue');
const PrivacyPolicyPage = () => import(/* webpackChunkName: "less-common" */ '../components/privacy-policy-page.vue');
const TermsOfUsePage = () => import(/* webpackChunkName: "less-common" */ '../components/terms-of-use-page.vue');
const HelpAndFAQPage = () => import(/* webpackChunkName: "less-common" */ '../components/faq-page.vue');
const PharmacistInformationPage = () => import(/* webpackChunkName: "less-common" */ '../components/pharmacist-information-page.vue');
const ProviderInformationPage = () => import(/* webpackChunkName: "less-common" */ '../components/provider-information-page.vue');
const BlogIndex = () => import(/* webpackChunkName: "less-common" */ '../components/blog-index.vue');
const NotFoundPage = () => import(/* webpackChunkName: "less-common" */ '../components/not-found-page.vue');
const ShouldIUseACoupon = () => import(/* webpackChunkName: "less-common" */ '../components/should-i-use-a-coupon.vue');
const MobileAppPage = () => import(/* webpackChunkName: "less-common" */ '../components/mobile-app-page.vue');

// If you are updating this list and want to allow analytics, check out App.vue method maybeDoAnalytics()
const routes = [
    {
        path: '/',
        name: 'home-page',
        component: HomePage,
        meta: {
            title: "IntelligentRx | Prescription Discount Coupon Search",
            description: "Save up to 85% on prescriptions at your local pharmacy. IntelligentRx finds the lowest prescription price at a pharmacy near you."
        }
    },
    {
        path: '/p',
        name: 'p-page',
        component: PPage,
        meta: {
            title: "IntelligentRx | Prescription Discount Coupon Search",
            description: "Save up to 85% on prescriptions at your local pharmacy. IntelligentRx finds the lowest prescription price at a pharmacy near you."
        }
    },
    {
        path: '/how-it-works',
        name: 'how-it-works-page',
        component: HowItWorksPage,
        meta: {
            title: "How it Works | IntelligentRx",
            description: "We provide the best savings on prescriptions to help everyone afford their medications. How does IntelligentRx work?"
        }
    },
    {
        path: '/for-members',
        name: 'savings-for-members',
        component: SavingsForMembers,
        meta: {
            title: "Savings for Members | IntelligentRx",
            description: "Reduce your medication spending"
        }
    },
    {
        path: '/for-employers',
        name: 'savings-for-employers',
        component: SavingsForEmployers,
        meta: {
            title: "Savings for Employers | IntelligentRx",
            description: "Reduce your prescription benefit spending"
        }
    },
    {
        path: '/for-benefit-consultants',
        name: 'benefit-consultants',
        component: BenefitConsultants,
        meta: {
            title: "Benefit Consultants | IntelligentRx",
            description: "Reduce prescription benefit spending"
        }
    },
    {
        path: '/for-health-plans',
        name: 'health-plans',
        component: PBMs,
        meta: {
            title: "Employer Savings for PBMs | IntelligentRx",
            description: "Reduce your prescription benefit spending"
        }
    },
    {
        path: '/for-payers',
        name: 'for-payers',
        component: ForPayers,
        meta: {
            title: "Payer Savings | IntelligentRx",
            description: "Improve Outcomes & Reduce Spending"
        }
    },
    {
        path: '/request-a-demo',
        name: 'request-a-demo',
        component: RequestADemo,
        meta: {
            title: "Request a Demo | IntelligentRx",
            description: "Request a demonstration of our premier services",
        }
    },
    {
        path: '/coupon-api',
        name: 'coupon-api',
        component: CouponAPI,
        meta: {
            title: "Coupon API | IntelligentRx",
        }
    },
    {
        path: '/api-contact',
        name: 'api-contact',
        component: APIContact,
        meta: {
            title: "Contact Us | API | IntelligentRx"
        }
    },
    {
        path: '/about',
        name: 'about-page',
        component: AboutPage,
        meta: {
            title: "About | IntelligentRx",
            description: "IntelligentRx is a healthcare savings innovation company. Our mission is to increase access to affordable healthcare to everyone."
        }
    },
    {
        path: '/careers',
        name: 'careers-page',
        component: CareersPage,
        meta: {
            title: "Careers | IntelligentRx",
            description: "IntelligentRx is a healthcare savings innovation company. Our mission is to increase access to affordable healthcare to everyone."
        }
    },
    {
        path: '/privacy-policy',
        name: 'privacy-policy-page',
        component: PrivacyPolicyPage,
        meta: {
            title: "Privacy Policy | IntelligentRx",
            description: "IntelligentRx's Privacy Policy"
        }
    },
    {
        path: '/terms-of-use',
        name: 'terms-of-use-page',
        component: TermsOfUsePage,
        meta: {
            title: "Terms of Use | IntelligentRx",
            description: "IntelligentRx's Terms of Use"
        }
    },
    {
        path: '/faq',
        name: 'faq-page',
        component: HelpAndFAQPage,
        meta: {
            title: "Frequently Asked Questions | IntelligentRx",
            description: "Frequently Asked Questions about prescription savings and IntelligentRx"
        }
    },
    {
        path: '/consumer-discount-cards',
        name: 'consumer-discount-cards-page',
        component: ConsumerDiscountCardsPage,
        meta: {
            title: "Consumer Discount Cards | IntelligentRx",
            description: "IntelligentRx's Consumer Discount Cards"
        }
    },
    {
        path: '/pharmacist-information',
        name: 'pharmacist-information-page',
        component: PharmacistInformationPage,
        meta: {
            title: "Pharmacist Information | IntelligentRx",
            description: "IntelligentRx's Pharmacist Information"
        }
    },
    {
        path: '/provider-information',
        name: 'provider-information-page',
        component: ProviderInformationPage,
        meta: {
            title: "Provider Information | IntelligentRx",
            description: "IntelligentRx's Provider Information"
        }
    },
    {
        path: '/contact',
        name: 'contact-page',
        component: ContactPage,
        meta: {
            title: "Contact | IntelligentRx",
            description: "IntelligentRx's Contact"
        }
    },
    {
        path: '/coupon',
        name: 'coupon-details-page',
        component: CouponDetailsPage,
        props: true,
        meta: {
            title: "Prescription Discount Coupon | IntelligentRx",
            description: "View a prescription discount coupon"
        }
    },
    {
        path: '/manufacturer-coupon',
        name: 'manufacturer-coupon-page',
        component: ManufacturerCouponPage,
        props: true,
        meta: {
            title: "Manufacturer Coupon | IntelligentRx",
            description: "View a manufacturer coupon"
        }
    },
    {
        path: '/alternative-medication',
        name: 'alternative-medication-page',
        component: AlternativeMedicationPage,
        props: true,
        meta: {
            title: "Alternative Medication | IntelligentRx",
            description: "View an alternative prescription medication",
        }
    },
    {
        path: '/local',
        name: 'local-page',
        component: LocalPage,
        props: true,
        meta: {
            title: "",
            description: "",
        }
    },
    {
        path: '/blog/:id',
        name: 'blog-post',
        props: true,
        component: BlogPost,
        meta: {
            title: "Blog | IntelligentRx",
            description: "",
        }
    },
    {
        path: '/blog',
        name: 'blog-index',
        component: BlogIndex,
        meta: {
            title: "Blog | IntelligentRx",
            description: "",
        }
    },
    {
        path: '/goodrx-comparison',
        name: 'goodrx-comparison-page',
        component: GoodRxComparisonPage,
        meta: {
            title: "GoodRx vs. IntelligentRx 2022",
            description: "Search for prescription drug prices from IntelligentRx and GoodRx and compare to save money on your prescriptions! IntellientRx is a prescription discount savings coupon service which connects YOU with the biggest and best prescription discounts."
        }
    },
    {
        path: '/cvs-savings-finder-comparison',
        name: 'cvs-savings-finder-comparison',
        component: CVSSavingsFinderComparison,
        meta: {
            title: "CVS Savings Finder Comparison",
            description: "Search for prescription drug prices from IntelligentRx and the CVS Savings Finder Comparison to find out which one is better! IntellientRx is a prescription discount savings coupon service which connects YOU with the biggest and best prescription discounts."
        }
    },
    {
        path: '/optum-perks-comparison',
        name: 'optum-perks-comparison',
        component: OptumPerksComparison,
        meta: {
            title: "Optum Perks Comparison",
            description: "Search for prescription drug prices from IntelligentRx and the Optum Perks Site to find out which one is better! IntellientRx is a prescription discount savings coupon service which connects YOU with the biggest and best prescription discounts",
        }
    },
    {
        path: '/aws-marketplace/register-customer',
        name: 'aws-marketplace-register-customer',
        component: AWSMarketplaceRegisterCustomer,
        meta: {
            title: "Register Customer | Marketplace | IntelligentRx",
            description: "Register as an IntelligentRx customer after purchasing access to our product from a Marketplace"
        }
    },
    {
        path: '/aws-marketplace/resolve-customer-error',
        name: 'aws-marketplace-resolve-customer-error',
        component: AWSMarketplaceResolveCustomerError,
        meta: {
            title: "Resolve Customer Error | Marketplace | IntelligentRx",
            description: "Error page shown when a customer's purchase of an IntelligentRx product via a Marketplace fails to work"
        }
    },
    {
        path: '/should-i-use-a-coupon',
        name: 'should-i-use-a-coupon',
        component: ShouldIUseACoupon,
        meta: {
            title: "Should I Use a Coupon?",
            description: "Work out if you should use a prescription discount coupon or not."
        }
    },
    {
        path: '/mobile-app',
        name: 'mobile-app',
        component: MobileAppPage,
        meta: {
            title: "IntelligentRx Mobile App",
            description: "Link to IntelligentRx's Mobile App for downloading"
        }
    },
    {
        path: '/do-not-overcharge-for-prescriptions',
        name: 'do-not-overcharge-for-prescriptions',
        component: TechLandingPage,
        meta: {
            title: "Don't Overcharge For Prescriptions",
            description: "With lower pharmacy fees than GoodRx and better or comparable prices, IntelligentRx is better for your pharmacy and better for consumers, and we have the analyses to prove it.",
        }
    },
    {
        path: '/reimburse-me',
        name: 'ReimburseMe',
        component: ReimburseMe,
        meta: {
            title: "Medical Insurance Reimbursement | IntelligentRx",
            description: "Learn how to be reimbursed for medical expenses which you paid for out of pocket"
        }
    },
    {
        path: '/prescription-transfer',
        name: 'PrescriptionTransfer',
        component: PrescriptionTransfer,
        meta: {
            title: "Prescription Transfer | IntelligentRx",
            description: "Transfer your prescription using this page",
        }
    },
    {
        // path: '*',
        path: "/:catchAll(.*)",
        name: 'not-found-page',
        component: NotFoundPage,
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    // https://router.vuejs.org/guide/advanced/scroll-behavior.html
    scrollBehavior(to, from, savedPosition): ScrollToOptions {
        // savedPosition is only available for popstate navigations.
        if (savedPosition != null) return savedPosition

        // if the returned position is falsy or an empty object,
        // will retain current scroll position.
        //@ts-ignore
        const emptyPosition: any = {};
        if (to.params.savePosition != null) return emptyPosition

        // scroll to anchor by returning the selector
        if (to.hash != null && to.hash !== "") {
            const position: any = { selector: to.hash }

            return position
        }

        // scroll to top by default
        const p: any = { left: 0, top: 0 };
        return p;
    }
})

router.beforeEach((to, from) => {
    if (from.fullPath === '/' || from.fullPath === '/after-auth')
        return;
    cookiemixin.methods.setCookie("irx-last-page", from.fullPath);
    return true;
});

router.afterEach((to) => {
    try {
        let routeIsForADrug = true;
        for (let i = 0; i < routes.length; i++) {
            const route = routes[i];
            if (to.path == route.path) {
                routeIsForADrug = false;
                break;
            }
        }
        if (!routeIsForADrug && !to.fullPath.startsWith('/admin') && to.meta != null && to.meta.title !== '') {
            //@ts-expect-error
            document.title = to.meta.title;
            const d = document.querySelector('head meta[name="description"]');
            if (d == null) {
                return;
            }
            //@ts-expect-error
            d.setAttribute('content', to.meta.description);
        }
    } catch (_) {
        // pass
    }
});

export default router
